/**
 * useUserStickies Hook
 *
 * Manages loading and saving of user stickies data to PostgreSQL.
 * Uses userSettingsService for persistence with localStorage fallback.
 *
 * Features:
 * - Auto-load on mount
 * - Debounced content/title saves (500ms)
 * - Debounced position saves (300ms)
 * - Optimistic updates
 */

import { useEffect, useRef, useCallback } from 'react';
import { useUserStickiesStore, type DockDisplayMode } from '@shared/store/useUserStickiesStore';
import { getSetting, setSetting } from '@shared/services/userSettingsService';
import { logger } from '@shared/utils/logger';
import type { UserStickyNote, StickyPosition, StickySize } from '@shared/types/userStickies';
import { USER_STICKIES_NAMESPACE, USER_STICKIES_KEYS, DEFAULT_STICKY_SIZE } from '@shared/types/userStickies';

/**
 * Debounce configuration
 */
const CONTENT_DEBOUNCE_MS = 500;
const POSITION_DEBOUNCE_MS = 300;

/**
 * Hook for managing user stickies data
 */
export const useUserStickies = () => {
  // Use individual selectors to avoid infinite re-render loop
  // (Calling useUserStickiesStore() without selector returns a new object each render)
  const stickies = useUserStickiesStore((state) => state.stickies);
  const isLoading = useUserStickiesStore((state) => state.isLoading);
  const isInitialized = useUserStickiesStore((state) => state.isInitialized);
  const deleteModalOpen = useUserStickiesStore((state) => state.deleteModalOpen);
  const stickyToDelete = useUserStickiesStore((state) => state.stickyToDelete);
  const isDockCollapsed = useUserStickiesStore((state) => state.isDockCollapsed);
  const dockDisplayMode = useUserStickiesStore((state) => state.dockDisplayMode);

  // Actions are stable references (created once in store)
  const storeAddSticky = useUserStickiesStore((state) => state.addSticky);
  const storeSetContent = useUserStickiesStore((state) => state.setContent);
  const storeSetTitle = useUserStickiesStore((state) => state.setTitle);
  const storeSetPosition = useUserStickiesStore((state) => state.setPosition);
  const storeSetSize = useUserStickiesStore((state) => state.setSize);
  const storeToggleCollapsed = useUserStickiesStore((state) => state.toggleCollapsed);
  const storeSetCollapsed = useUserStickiesStore((state) => state.setCollapsed);
  const storeDeleteSticky = useUserStickiesStore((state) => state.deleteSticky);
  const storeSetLoading = useUserStickiesStore((state) => state.setLoading);
  const storeSetInitialized = useUserStickiesStore((state) => state.setInitialized);
  const storeHydrate = useUserStickiesStore((state) => state.hydrate);
  const storeOpenDeleteModal = useUserStickiesStore((state) => state.openDeleteModal);
  const storeCloseDeleteModal = useUserStickiesStore((state) => state.closeDeleteModal);
  const storeSetDockCollapsed = useUserStickiesStore((state) => state.setDockCollapsed);
  const storeToggleDockCollapsed = useUserStickiesStore((state) => state.toggleDockCollapsed);
  const storeSetDockDisplayMode = useUserStickiesStore((state) => state.setDockDisplayMode);
  const storeCycleDockDisplayMode = useUserStickiesStore((state) => state.cycleDockDisplayMode);

  const contentDebounceRef = useRef<Record<string, NodeJS.Timeout>>({});
  const stateDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const dockStateDebounceRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Save all stickies to PostgreSQL
   */
  const saveStickies = useCallback(async (stickies: UserStickyNote[]) => {
    try {
      await setSetting(USER_STICKIES_KEYS.STICKIES, stickies, {
        namespace: USER_STICKIES_NAMESPACE,
      });
      logger.log('[UserStickies] Saved stickies to PostgreSQL');
    } catch (error) {
      logger.error('[UserStickies] Failed to save stickies:', error);
    }
  }, []);

  /**
   * Save dock collapsed state to PostgreSQL (legacy)
   */
  const saveDockCollapsed = useCallback(async (collapsed: boolean) => {
    try {
      await setSetting(USER_STICKIES_KEYS.DOCK_COLLAPSED, collapsed, {
        namespace: USER_STICKIES_NAMESPACE,
      });
      logger.log('[UserStickies] Saved dock collapsed state to PostgreSQL');
    } catch (error) {
      logger.error('[UserStickies] Failed to save dock collapsed state:', error);
    }
  }, []);

  /**
   * Save dock display mode to PostgreSQL
   */
  const saveDockDisplayMode = useCallback(async (mode: DockDisplayMode) => {
    try {
      await setSetting('dock_display_mode', mode, {
        namespace: USER_STICKIES_NAMESPACE,
      });
      logger.log('[UserStickies] Saved dock display mode to PostgreSQL:', mode);
    } catch (error) {
      logger.error('[UserStickies] Failed to save dock display mode:', error);
    }
  }, []);

  /**
   * Debounced save for state changes (position, collapsed)
   */
  const debouncedSaveState = useCallback(() => {
    if (stateDebounceRef.current) {
      clearTimeout(stateDebounceRef.current);
    }

    stateDebounceRef.current = setTimeout(() => {
      const currentState = useUserStickiesStore.getState();
      saveStickies(currentState.stickies);
    }, POSITION_DEBOUNCE_MS);
  }, [saveStickies]);

  /**
   * Debounced save for content/title changes
   */
  const debouncedSaveContent = useCallback(
    (stickyId: string) => {
      const debounceKey = `content-${stickyId}`;
      if (contentDebounceRef.current[debounceKey]) {
        clearTimeout(contentDebounceRef.current[debounceKey]);
      }

      contentDebounceRef.current[debounceKey] = setTimeout(() => {
        const currentState = useUserStickiesStore.getState();
        saveStickies(currentState.stickies);
      }, CONTENT_DEBOUNCE_MS);
    },
    [saveStickies]
  );

  /**
   * Load stickies from PostgreSQL
   */
  const loadStickies = useCallback(async () => {
    // Use getState() to check current value without subscribing
    const currentState = useUserStickiesStore.getState();
    if (currentState.isInitialized) return;

    storeSetLoading(true);

    try {
      // Load stickies and dock display mode in parallel
      const [loadedStickies, loadedDockDisplayMode, loadedDockCollapsed] = await Promise.all([
        getSetting<UserStickyNote[]>(USER_STICKIES_KEYS.STICKIES, {
          namespace: USER_STICKIES_NAMESPACE,
          defaultValue: [],
        }),
        getSetting<DockDisplayMode>('dock_display_mode', {
          namespace: USER_STICKIES_NAMESPACE,
          defaultValue: 'full',
        }),
        // Also load legacy dock_collapsed for migration
        getSetting<boolean>(USER_STICKIES_KEYS.DOCK_COLLAPSED, {
          namespace: USER_STICKIES_NAMESPACE,
          defaultValue: false,
        }),
      ]);

      // Migrate legacy stickies that don't have size property
      const migratedStickies = (loadedStickies ?? []).map(sticky => ({
        ...sticky,
        size: sticky.size ?? { ...DEFAULT_STICKY_SIZE },
      }));

      storeHydrate(migratedStickies);

      // Use dock display mode if available, otherwise migrate from legacy dock_collapsed
      const effectiveMode = loadedDockDisplayMode ?? (loadedDockCollapsed ? 'hidden' : 'full');
      storeSetDockDisplayMode(effectiveMode);
      logger.log('[UserStickies] Loaded stickies from PostgreSQL, dockDisplayMode:', effectiveMode);
    } catch (error) {
      logger.error('[UserStickies] Failed to load stickies:', error);
      storeSetInitialized(true);
    } finally {
      storeSetLoading(false);
    }
  }, [storeSetLoading, storeHydrate, storeSetInitialized, storeSetDockDisplayMode]);

  /**
   * Add new sticky and save
   */
  const addSticky = useCallback(() => {
    const newId = storeAddSticky();
    // Save immediately after adding
    const currentState = useUserStickiesStore.getState();
    saveStickies(currentState.stickies);
    return newId;
  }, [storeAddSticky, saveStickies]);

  /**
   * Update sticky content with debounced save
   */
  const setContent = useCallback(
    (id: string, content: string) => {
      storeSetContent(id, content);
      debouncedSaveContent(id);
    },
    [storeSetContent, debouncedSaveContent]
  );

  /**
   * Update sticky title with debounced save
   */
  const setTitle = useCallback(
    (id: string, title: string) => {
      storeSetTitle(id, title);
      debouncedSaveContent(id);
    },
    [storeSetTitle, debouncedSaveContent]
  );

  /**
   * Update position with debounced save
   */
  const setPosition = useCallback(
    (id: string, position: StickyPosition) => {
      storeSetPosition(id, position);
      debouncedSaveState();
    },
    [storeSetPosition, debouncedSaveState]
  );

  /**
   * Update size with debounced save
   */
  const setSize = useCallback(
    (id: string, size: StickySize) => {
      storeSetSize(id, size);
      debouncedSaveState();
    },
    [storeSetSize, debouncedSaveState]
  );

  /**
   * Toggle collapsed state with debounced save
   */
  const toggleCollapsed = useCallback(
    (id: string) => {
      storeToggleCollapsed(id);
      debouncedSaveState();
    },
    [storeToggleCollapsed, debouncedSaveState]
  );

  /**
   * Set collapsed state with debounced save
   */
  const setCollapsed = useCallback(
    (id: string, collapsed: boolean) => {
      storeSetCollapsed(id, collapsed);
      debouncedSaveState();
    },
    [storeSetCollapsed, debouncedSaveState]
  );

  /**
   * Delete sticky (called from modal confirmation)
   */
  const deleteSticky = useCallback(
    (id: string) => {
      storeDeleteSticky(id);
      // Save immediately after deleting
      const currentState = useUserStickiesStore.getState();
      saveStickies(currentState.stickies);
    },
    [storeDeleteSticky, saveStickies]
  );

  /**
   * Confirm delete from modal
   */
  const confirmDelete = useCallback(() => {
    const { stickyToDelete: toDelete } = useUserStickiesStore.getState();
    if (toDelete) {
      deleteSticky(toDelete);
    }
    storeCloseDeleteModal();
  }, [storeCloseDeleteModal, deleteSticky]);

  /**
   * Toggle dock collapsed state with debounced save (legacy)
   */
  const toggleDockCollapsed = useCallback(() => {
    storeToggleDockCollapsed();

    // Debounce save to prevent rapid saves during multiple toggles
    if (dockStateDebounceRef.current) {
      clearTimeout(dockStateDebounceRef.current);
    }

    dockStateDebounceRef.current = setTimeout(() => {
      const currentState = useUserStickiesStore.getState();
      saveDockCollapsed(currentState.isDockCollapsed);
      saveDockDisplayMode(currentState.dockDisplayMode);
    }, POSITION_DEBOUNCE_MS);
  }, [storeToggleDockCollapsed, saveDockCollapsed, saveDockDisplayMode]);

  /**
   * Cycle dock display mode with debounced save (full -> menuOnly -> hidden -> full)
   */
  const cycleDockDisplayMode = useCallback(() => {
    storeCycleDockDisplayMode();

    // Debounce save to prevent rapid saves during multiple toggles
    if (dockStateDebounceRef.current) {
      clearTimeout(dockStateDebounceRef.current);
    }

    dockStateDebounceRef.current = setTimeout(() => {
      const currentState = useUserStickiesStore.getState();
      saveDockDisplayMode(currentState.dockDisplayMode);
    }, POSITION_DEBOUNCE_MS);
  }, [storeCycleDockDisplayMode, saveDockDisplayMode]);

  /**
   * Set dock display mode with debounced save
   */
  const setDockDisplayMode = useCallback((mode: DockDisplayMode) => {
    storeSetDockDisplayMode(mode);

    // Debounce save
    if (dockStateDebounceRef.current) {
      clearTimeout(dockStateDebounceRef.current);
    }

    dockStateDebounceRef.current = setTimeout(() => {
      saveDockDisplayMode(mode);
    }, POSITION_DEBOUNCE_MS);
  }, [storeSetDockDisplayMode, saveDockDisplayMode]);

  // Auto-load on mount
  useEffect(() => {
    loadStickies();

    // Cleanup debounce timers on unmount
    return () => {
      Object.values(contentDebounceRef.current).forEach(clearTimeout);
      if (stateDebounceRef.current) {
        clearTimeout(stateDebounceRef.current);
      }
      if (dockStateDebounceRef.current) {
        clearTimeout(dockStateDebounceRef.current);
      }
    };
  }, [loadStickies]);

  return {
    // State
    stickies,
    isLoading,
    isInitialized,
    deleteModalOpen,
    stickyToDelete,
    isDockCollapsed,
    dockDisplayMode,

    // Actions
    addSticky,
    setContent,
    setTitle,
    setPosition,
    setSize,
    toggleCollapsed,
    setCollapsed,
    deleteSticky,

    // Dock display mode
    setDockDisplayMode,
    cycleDockDisplayMode,

    // Dock collapse (legacy)
    toggleDockCollapsed,

    // Delete modal
    openDeleteModal: storeOpenDeleteModal,
    closeDeleteModal: storeCloseDeleteModal,
    confirmDelete,

    // Load/save
    loadStickies,
  };
};
